// Project-refs popover that hangs off the composer's 📎 button. Lists
// every file currently in the project's `_refs/`; supports attach (mention
// in current draft), peek (read text), delete, and upload-new. Exists
// because the previous sidebar RefsPanel was hard to discover during a
// conversation — refs ARE project-scoped, but they're consumed in chat,
// so the management UI should live there too.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RefEntry } from '../api';
import { api } from '../api';

interface Props {
  projectId: string;
  /** Add a filename to the composer's attached-refs chip row. The composer
   *  decides whether to dedupe (it does). */
  onAttach: (name: string) => void;
  /** Close the popover. Called on outside click, Escape, or after picking
   *  upload (with files chosen, the user has obviously committed). */
  onClose: () => void;
}

export function RefsPopover({ projectId, onAttach, onClose }: Props) {
  const { t } = useTranslation();
  const [refs, setRefs] = useState<RefEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [peeked, setPeeked] = useState<{ name: string; body: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listRefs(projectId);
      setRefs(list);
    } catch (e) {
      setError(t('refs.errLoad', { message: (e as Error).message }));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Outside-click + Escape close. Skip while a peek modal is open — that
  // modal's backdrop click also bubbles here, so closing the popover under
  // it would steal focus.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (peeked) return;
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (peeked) setPeeked(null);
        else onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, peeked]);

  const onUpload = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setError(null);
    setUploading((n) => n + arr.length);
    for (const f of arr) {
      try {
        const entry = await api.uploadRef(projectId, f);
        setRefs((prev) => {
          const without = prev.filter((r) => r.name !== entry.name);
          return [...without, entry];
        });
        // Auto-attach so a fresh upload from inside the popover lands in
        // the draft's chip row immediately — that's the workflow ("I'm
        // about to talk about this file") the popover is for.
        onAttach(entry.name);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
  };

  const onDelete = async (name: string) => {
    if (!confirm(t('refs.confirmDelete', { name }))) return;
    try {
      await api.deleteRef(projectId, name);
      setRefs((prev) => prev.filter((r) => r.name !== name));
    } catch (e) {
      setError(t('refs.errDelete', { message: (e as Error).message }));
    }
  };

  const onPeek = async (name: string) => {
    try {
      const body = await api.readRef(projectId, name);
      setPeeked({ name, body });
    } catch (e) {
      setError(t('refs.errRead', { message: (e as Error).message }));
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          // Anchor above the 📎 button. Caller sets a positioning context
          // on its own wrapper; we just need to push up + a bit of breathing
          // room from the button it's attached to.
          bottom: 'calc(100% + 6px)',
          right: 0,
          zIndex: 60,
          background: 'white',
          border: '1px solid var(--rule)',
          borderRadius: 6,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          minWidth: 320,
          maxWidth: 380,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header — explicit "PROJECT" so users don't think these are
            thread-scoped. Same labeling rule as the popover existing at all. */}
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.1em',
            fontWeight: 700,
            padding: '8px 10px 6px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {t('refs.heading')}
          {refs.length > 0 && (
            <span style={{ color: 'var(--ink-2)', fontWeight: 400 }}>
              · {refs.length}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ color: 'var(--ink-3)', fontWeight: 400, fontSize: 9.5 }}>
            {t('refs.sharedAcrossThreads')}
          </span>
        </div>

        {/* Body */}
        <div
          style={{
            padding: 4,
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--ink-3)',
                padding: '12px 8px',
                fontStyle: 'italic',
              }}
            >
              {t('common.loading')}
            </div>
          ) : refs.length === 0 ? (
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--ink-3)',
                padding: '14px 8px',
                fontStyle: 'italic',
                textAlign: 'center',
              }}
            >
              {t('refs.emptyState')}
            </div>
          ) : (
            refs.map((r) => (
              <div
                key={r.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 8px',
                  fontSize: 12,
                  borderRadius: 4,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--paper-1)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'var(--ink-1)',
                  }}
                  title={r.name}
                >
                  📄 {r.name}
                </span>
                <span
                  className="mono"
                  style={{ fontSize: 9.5, color: 'var(--ink-3)' }}
                >
                  {formatSize(r.size_bytes)}
                </span>
                <button
                  onClick={() => {
                    onAttach(r.name);
                  }}
                  className="mono"
                  title={t('refs.attachTitle')}
                  style={{
                    fontSize: 10,
                    padding: '1px 6px',
                    border: '1px solid var(--rule)',
                    borderRadius: 3,
                    background: 'white',
                    color: 'var(--ink-1)',
                    cursor: 'pointer',
                  }}
                >
                  {t('refs.attach')}
                </button>
                <button
                  onClick={() => onPeek(r.name)}
                  title={t('refs.peekTitle')}
                  style={{
                    fontSize: 12,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--ink-3)',
                    cursor: 'pointer',
                    padding: '0 4px',
                  }}
                >
                  👁
                </button>
                <button
                  onClick={() => onDelete(r.name)}
                  title={t('common.delete')}
                  style={{
                    fontSize: 13,
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--ink-3)',
                    cursor: 'pointer',
                    padding: '0 4px',
                  }}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer — upload zone */}
        <div
          style={{
            borderTop: '1px solid var(--rule)',
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) {
                void onUpload(e.target.files);
                e.currentTarget.value = '';
              }
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="mono"
            style={{
              fontSize: 11,
              padding: '4px 10px',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              background: 'white',
              color: 'var(--ink-1)',
              cursor: 'pointer',
            }}
          >
            {t('refs.uploadNew')}
          </button>
          {uploading > 0 && (
            <span
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--ink-3)' }}
            >
              {t('refs.uploading', { count: uploading })}
            </span>
          )}
          {error && (
            <span
              className="mono"
              onClick={() => setError(null)}
              style={{
                fontSize: 10.5,
                color: 'var(--red, #d23f3f)',
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
              title={t('refs.dismissError')}
            >
              {error}
            </span>
          )}
        </div>
      </div>

      {/* Peek overlay — same shape as the legacy RefsPanel preview, kept
          inline because no other consumer needs it. */}
      {peeked && (
        <div
          onClick={() => setPeeked(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 200,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--paper-0, white)',
              borderRadius: 6,
              padding: 16,
              maxWidth: '80vw',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 24px rgba(0,0,0,.18)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                marginBottom: 10,
                paddingBottom: 8,
                borderBottom: '1px solid var(--rule)',
              }}
            >
              <strong style={{ fontSize: 13 }}>{peeked.name}</strong>
              <span style={{ flex: 1 }} />
              <button
                onClick={() => setPeeked(null)}
                style={{
                  fontSize: 12,
                  border: '1px solid var(--rule)',
                  borderRadius: 3,
                  padding: '2px 8px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                {t('common.close')}
              </button>
            </div>
            <pre
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'var(--ink-1)',
                margin: 0,
              }}
            >
              {peeked.body}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
